<template>
  <div>
    <hr />
    <el-dialog
      v-model="dialogVisible"
      title="Confirm action"
      width="20%"
      :modal="false"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      @close="closeModal"
    >
      <div class="modal-content">
        <span class="modal-text"> {{ ActionText[selectedAction] }} </span>
        <template v-if="selectedAction">
          <VerticalSelector
            v-if="selectedAction === Actions.Vertical"
            v-model="verticalValue"
            :disabled="false"
            @update:modelValue="update"
          />
        </template>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button type="primary" @click="submit"> Confirm </el-button>
        </span>
      </template>
    </el-dialog>
    <div class="mass-actions">
      <span class="mass-actions__label">With selected:</span>
      <el-button
        v-for="action in Actions"
        :key="action"
        :type="ActionColors[action]"
        :icon="ActionIcons[action]"
        plain
        :disabled="massActionDisables"
        size="small"
        @click="setDialog(action)"
      >
        {{ ActionLabels[action] }}
      </el-button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import { useCreativesStore } from '@/apps/traforama/store/creatives'
import VerticalSelector from '@/apps/traforama/views/creatives/components/VerticalSelector.vue'
import { ElMessage } from 'element-plus'
import { safeSubmitProcessing } from '@/utils/helpers'

enum Actions {
  Vertical = 'vertical',
  Approve = 'approve',
  Reject = 'reject'
}

const ActionLabels: { [title in Actions]: string } = {
  [Actions.Vertical]: 'Change vertical',
  [Actions.Approve]: 'Approve',
  [Actions.Reject]: 'Reject'
}

const ActionColors: { [title in Actions]: string } = {
  [Actions.Vertical]: 'primary',
  [Actions.Approve]: 'warning',
  [Actions.Reject]: 'info'
}

const ActionIcons: { [title in Actions]: string } = {
  [Actions.Vertical]: 'EditElementIcon',
  [Actions.Approve]: 'CheckElementIcon',
  [Actions.Reject]: 'CircleCloseElementIcon'
}

const ActionText: { [title in Actions]: string } = {
  [Actions.Vertical]: 'Select new vertival type',
  [Actions.Approve]: 'Are you sure you want to approve selected creatures',
  [Actions.Reject]: 'Are you sure you want to reject selected creatures'
}

export default defineComponent({
  name: 'MassActions',
  components: {
    VerticalSelector
  },
  setup() {
    const creativesStore = useCreativesStore()
    const dialogVisible = ref(false)
    const selectedAction = ref('')
    const value = ref('')
    const loading = ref(false)

    const verticalValue = ref('')

    const massActionDisables = computed(() => {
      return Object.keys(creativesStore.selected).length < 1
    })

    const submit = async () => {
      await safeSubmitProcessing(
        loading,
        () =>
          creativesStore.MassUpdateItems(
            Object.keys(creativesStore.selected),
            selectedAction.value,
            value.value
          ),
        () => onSuccess(),
        (err) => onError(err)
      )
    }

    const onSuccess = () => {
      ElMessage.success({
        message: 'Event has been successfully done!',
        type: 'success'
      })
      closeModal()
      creativesStore.ClearSelected()
    }

    const onError = (err: any) => {
      ElMessage.error({
        message: err.response.data.message,
        type: 'error',
        duration: 5 * 1000
      })
      closeModal()
    }

    const update = (newValue?: any) => {
      value.value = Array.isArray(newValue) ? newValue[1] : newValue
    }

    const closeModal = () => {
      value.value = ''
      dialogVisible.value = false
    }

    const setDialog = (action: string) => {
      selectedAction.value = action
      dialogVisible.value = true
    }

    return {
      massActionDisables,
      dialogVisible,
      setDialog,
      submit,
      ActionLabels,
      ActionIcons,
      Actions,
      ActionColors,
      selectedAction,
      ActionText,
      update,
      closeModal,
      value,
      verticalValue
    }
  }
})
</script>

<style lang="scss" scoped>
.modal-content {
  display: flex;
  flex-direction: column;
}

.mass-action {
  display: flex;
  justify-content: center;
}

.mass-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0;
}

.mass-actions__label {
  margin-right: 4px;
  color: var(--el-text-color-secondary);
  font-size: 12px;
  white-space: nowrap;
}

.modal-text {
  margin-bottom: 10px;
}
</style>
